import React, { useState, useCallback, useMemo } from 'react';
import { KatProgress, KatButton } from '@amzn/katal-react';
import { useUser } from 'src/utils/UserContext';
import { useEntireNetworkBulkR2FData } from '../../hooks/useBulkR2FData';
import { BulkR2FTable } from '../../components/bulkR2F/BulkR2FTable';
import { R2FUploadButton } from '../../components/bulkR2F/R2FUploadButton';
import { PendingThresholdChange } from 'src/features/types/r2fTypes';
import { publishR2FThresholds } from 'src/utils/Queries';
import { getDisplayName } from '../../utils/displayUtils';
import { exportR2FDataToCSV, downloadCSV } from '../../utils/r2fCsvHelpers';
import { useDefaultPCSafety } from '../../hooks/useDefaultPCSafety';
import { validateR2FBulkRequestForDefaultPC } from '../../utils/defaultPCSafety';
import SafetyWarning from '../../components/common/SafetyWarning';
import './BulkR2FManager.css';

/**
 * Bulk R2F Manager Page Component
 *
 * This page allows users to view and manage R2F threshold configurations
 * across multiple warehouses, organized by handling tier with collapsible FC rows.
 */
const BulkR2FManager: React.FC = () => {
    const { user } = useUser();
    
    // Use the new entire network API that doesn't require warehouse IDs
    const { data, networkDefaults, isLoading, error, refetch } = useEntireNetworkBulkR2FData(true);
    
    // State for managing pending changes and UI
    const [pendingChanges, setPendingChanges] = useState<PendingThresholdChange[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    // Initialize safety hook for Default_PC protection
    const safety = useDefaultPCSafety({
        operationType: 'R2F',
        userAlias: user?.login,
        warehouseId: 'Bulk Operation'
    });
    const [networkDefaultsCollapsed, setNetworkDefaultsCollapsed] = useState(false);

    // Check if user can edit
    const userCanEdit = user?.isAuthorizedForSLAOverrides || user?.isDevAdmin || false;

    // Handle saving threshold changes - reuse the same logic as useR2FThresholds
    const handleSave = useCallback(async (changes: PendingThresholdChange[]) => {
        if (!userCanEdit || changes.length === 0) {
            return;
        }
        
        // Validate high > low threshold for each change
        for (const change of changes) {
            if (change.lowThreshold !== undefined && change.highThreshold !== undefined) {
                if (change.highThreshold <= change.lowThreshold) {
                    setErrorMessage(`High threshold (${change.highThreshold}) must be greater than low threshold (${change.lowThreshold}) for ${change.warehouseId}/${change.productionCapability}/${change.handlingTier}`);
                    return;
                }
            }
        }
        
        // CRITICAL SAFETY CHECK: Validate for Default_PC entries
        const safetyCheck = safety.validateSafety(changes);
        
        if (safetyCheck.shouldBlock) {
            // Block the save operation and show error message
            safety.showWarning(safetyCheck.errorMessage, 'error');
            
            // Log the safety violation for audit purposes
            safety.logViolation(safetyCheck.defaultPCCount);
            
            // Do not proceed with the save
            return;
        }

        setIsSaving(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            // Group changes by warehouse for efficient API calls
            const changesByWarehouse = groupChangesByWarehouse(changes);

            // Process each warehouse's changes using the same logic as useR2FThresholds
            for (const [warehouseId, warehouseChanges] of Object.entries(changesByWarehouse)) {
                await processWarehouseChanges(warehouseId, warehouseChanges);
            }

            // Handle successful save
            await handleSaveSuccess(changes, changesByWarehouse);

        } catch (err) {
            setErrorMessage('Failed to save changes. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }, [userCanEdit, user?.login, refetch]);

    // Group changes by warehouse for efficient API calls
    const groupChangesByWarehouse = (changes: PendingThresholdChange[]) => {
        return changes.reduce((acc, change) => {
            const warehouseId = change.warehouseId || 'DEFAULT';
            if (!acc[warehouseId]) {
                acc[warehouseId] = [];
            }
            acc[warehouseId].push(change);
            return acc;
        }, {} as Record<string, PendingThresholdChange[]>);
    };

    // Process changes for a specific warehouse
    const processWarehouseChanges = async (warehouseId: string, warehouseChanges: PendingThresholdChange[]) => {
        const userLogin = user?.login;
        if (!userLogin) {
            throw new Error('User login is required for saving changes');
        }
        
        const r2fToPublishList = prepareThresholdPublishRequest(warehouseChanges, warehouseId, userLogin);
        
        // ADDITIONAL SAFETY CHECK: Validate the final publish request for Default_PC
        const bulkValidation = validateR2FBulkRequestForDefaultPC(r2fToPublishList);
        
        if (bulkValidation.hasDefaultPC) {
            // This should not happen if the earlier validation worked, but it's a safety net
            throw new Error(`CRITICAL SAFETY ERROR: Default_PC detected in bulk publish request for warehouse ${warehouseId}. Operation aborted.`);
        }
        
        await publishR2FThresholds({ r2fToPublishList: bulkValidation.filteredItems });
    };

    // Handle successful save completion
    const handleSaveSuccess = async (changes: PendingThresholdChange[], changesByWarehouse: Record<string, PendingThresholdChange[]>) => {
        setPendingChanges([]);
        setSuccessMessage(`Successfully updated ${changes.length} threshold${changes.length > 1 ? 's' : ''} across ${Object.keys(changesByWarehouse).length} warehouse${Object.keys(changesByWarehouse).length > 1 ? 's' : ''}`);
        await refetch();
    };

    // Reuse the same transformation logic from useR2FThresholds
    const prepareThresholdPublishRequest = (
        changes: PendingThresholdChange[],
        warehouseId: string,
        userAlias: string
    ) => {
        // Filter out deletion changes (those will be handled separately)
        const nonDeletionChanges = changes.filter(change => change.action !== 'Delete');
        
        // Group changes by production capability
        const changesByPC: Record<string, PendingThresholdChange[]> = {};
        
        // Process non-deletion changes
        nonDeletionChanges.forEach(change => {
            const pc = change.productionCapability;
            if (!changesByPC[pc]) {
                changesByPC[pc] = [];
            }
            changesByPC[pc].push(change);
        });
        
        // Create the publish request
        const thresholdsToPublish: any = {};
        
        // Process each production capability
        Object.entries(changesByPC).forEach(([pc, pcChanges]) => {
            if (!thresholdsToPublish[pc]) {
                thresholdsToPublish[pc] = {};
            }
            
            // Process each change for this production capability
            pcChanges.forEach(change => {
                // Add or modify
                const threshold = {
                    lowThreshold: change.lowThreshold,
                    highThreshold: change.highThreshold,
                    userAlias: change.userAlias || userAlias,
                    reason: change.reason || 'Bulk R2F Update',
                    publishDateEpochMillis: Date.now(),
                    actionSource: change.actionSource || 'Newton Dashboard Bulk R2F'
                };
                
                thresholdsToPublish[pc][change.handlingTier] = threshold;
            });
        });
        
        // Create the final request for non-deletion changes
        return [{
            warehouseId,
            r2fToPublish: thresholdsToPublish
        }];
    };

    // Handle CSV export
    const handleExportCSV = useCallback(() => {
        if (!data || data.length === 0) {
            setErrorMessage('No data available to export');
            return;
        }

        try {
            const csvContent = exportR2FDataToCSV(data);
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filename = `r2f-thresholds-${timestamp}.csv`;
            downloadCSV(csvContent, filename);
            setSuccessMessage('CSV exported successfully');
        } catch (error) {
            console.error('Error exporting CSV:', error);
            setErrorMessage('Failed to export CSV. Please try again.');
        }
    }, [data]);

    // Handle clearing messages
    const clearMessages = useCallback(() => {
        setSuccessMessage('');
        setErrorMessage('');
        safety.dismissWarning();
    }, []);

    // Show loading state
    if (isLoading) {
        return (
            <div className="bulk-r2f-loading">
                <KatProgress indeterminate />
                <p>Loading TRB threshold configurations...</p>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="bulk-r2f-manager">
                <h1>Bulk TRB Threshold Configuration</h1>
                <div className="error-state">
                    <p>Error loading TRB data: {error}</p>
                    <button onClick={refetch} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Show empty state only if both data and networkDefaults are empty
    if ((!data || data.length === 0) && (!networkDefaults || Object.keys(networkDefaults).length === 0)) {
        return (
            <div className="bulk-r2f-manager">
                <h1>Bulk TRB Threshold Configuration</h1>
                <div className="empty-state">
                    <p>No TRB threshold configurations found in the network.</p>
                    <p>This may indicate a data availability issue or that no thresholds have been configured.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bulk-r2f-manager">
            <div className="bulk-r2f-header">
                <h1>Bulk TRB Threshold Configuration</h1>
                <div className="bulk-r2f-description">
                    <p>
                        Configure Tiny-Red-Button (TRB) thresholds across multiple warehouses.
                        Data is organized by handling tier with collapsible warehouse sections showing their production capabilities.
                    </p>
                </div>
            </div>

            {/* Safety Warning Display */}
            {safety.warningState?.isVisible && (
                <SafetyWarning
                    message={safety.warningState.message}
                    type={safety.warningState.type}
                    operationType={safety.warningState.operationType}
                    onDismiss={safety.dismissWarning}
                />
            )}

            {/* Success/Error Messages */}
            {successMessage && (
                <div className="success-message">
                    <span>{successMessage}</span>
                    <button onClick={clearMessages} className="close-button">×</button>
                </div>
            )}
            
            {errorMessage && (
                <div className="error-message">
                    <span>{errorMessage}</span>
                    <button onClick={clearMessages} className="close-button">×</button>
                </div>
            )}

            {/* Controls Section */}
            <div className="bulk-r2f-controls">
                <div className="warehouse-info">
                    <span>Warehouses: All network warehouses</span>
                    <span>Tiers: {data.length} with data</span>
                    <span>Network Defaults: {Object.keys(networkDefaults).length} tiers</span>
                </div>
                
                <div className="bulk-actions">
                    <KatButton
                        variant="primary"
                        onClick={handleExportCSV}
                        disabled={!data || data.length === 0}
                    >
                        Export CSV
                    </KatButton>
                    {userCanEdit && <R2FUploadButton />}
                </div>
            </div>

            {/* Network Defaults Section */}
            {Object.keys(networkDefaults).length > 0 && (
                <div className="network-defaults-section">
                    <h2 onClick={() => setNetworkDefaultsCollapsed(!networkDefaultsCollapsed)}>
                        Network Default Thresholds
                        <span className="network-defaults-toggle">
                            {networkDefaultsCollapsed ? '▼' : '▲'}
                        </span>
                    </h2>
                    {!networkDefaultsCollapsed && (
                        <>
                            <p className="network-defaults-description">
                                These thresholds apply network-wide and are the same across all fulfillment centers.
                            </p>
                            
                            <div className="network-defaults-grid">
                                {Object.entries(networkDefaults).map(([tierName, capabilities]) => (
                                    <div key={tierName} className="network-default-tier">
                                        <h3 className="network-tier-name">{tierName}</h3>
                                        <div className="network-capabilities">
                                            {capabilities.map((capability) => (
                                                <div key={capability.name} className="network-capability">
                                                    <div className="capability-name">{getDisplayName(capability.name)}</div>
                                                    <div className="capability-thresholds">
                                                        <span className="threshold-value">
                                                            Low: {capability.lowThreshold ?? '—'}
                                                        </span>
                                                        <span className="threshold-value">
                                                            High: {capability.highThreshold ?? '—'}
                                                        </span>
                                                    </div>
                                                    {capability.publishDateEpochMillis && (
                                                        <div className="capability-meta">
                                                            Updated: {new Date(capability.publishDateEpochMillis).toLocaleDateString()}
                                                            {capability.userAlias && ` by ${capability.userAlias}`}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Main Table */}
            {data && data.length > 0 ? (
                <BulkR2FTable
                    data={data}
                    onSave={handleSave}
                    userCanEdit={userCanEdit}
                    isLoading={isSaving}
                    pendingChanges={pendingChanges}
                    onPendingChangesUpdate={setPendingChanges}
                    userLogin={user?.login}
                />
            ) : (
                <div className="no-fc-data-message">
                    <h3>Fulfillment Center Specific Thresholds</h3>
                    <p>No FC-specific TRB threshold configurations found for the selected warehouses.</p>
                    <p>Only network-wide default thresholds are available above.</p>
                </div>
            )}

            {/* Loading overlay during save */}
            {isSaving && (
                <div className="saving-overlay">
                    <div className="saving-content">
                        <KatProgress indeterminate />
                        <p>Saving changes...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BulkR2FManager;
